// ── participant-sessions-collection: bounded client adapter (no urlSync) ───
// Own-cohort session rows for the participant dashboard Sessions section. Scoped
// to the learner's enrolment — not a directory surface, so collection state stays
// in-memory (urlSync:false).

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import { sessionStatusLabel, sessionStatusTone } from "../sessions/sessions-helpers";

export const PARTICIPANT_SESSIONS_PAGE_SIZE = 25;
export const PARTICIPANT_SESSIONS_DEFAULT_SORT = { id: "startsAt", dir: "asc" as const };

export type ParticipantSessionRow = {
  id: string;
  title: string;
  cohort: string;
  when: string;
  startsAt: string;
  status: string;
  statusRaw: string | undefined;
  recording: string;
};

export function buildParticipantSessionsFilterDefs(
  rows: ParticipantSessionRow[]
): CollectionFilterDef<ParticipantSessionRow>[] {
  const statuses = Array.from(new Set(rows.map((row) => row.statusRaw).filter(Boolean) as string[])).sort();
  if (statuses.length === 0) return [];

  return [
    {
      id: "statusRaw",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.statusRaw ?? "",
      options: statuses.map((status) => ({ value: status, label: sessionStatusLabel(status) }))
    }
  ];
}

export function buildParticipantSessionsCollectionColumns(): CollectionColumnDef<ParticipantSessionRow>[] {
  return [
    {
      id: "title",
      header: "Session",
      sortable: true,
      sortAccessor: (row) => row.title,
      searchValue: (row) => `${row.title} ${row.cohort} ${row.when}`,
      priority: 1,
      cell: (row) => <span className="font-medium text-strong">{row.title}</span>
    },
    {
      id: "cohort",
      header: "Cohort",
      sortable: true,
      sortAccessor: (row) => row.cohort,
      priority: 2,
      cell: (row) => <span>{row.cohort}</span>
    },
    {
      id: "when",
      header: "When (your time)",
      sortable: true,
      sortAccessor: (row) => row.startsAt,
      priority: 2,
      cell: (row) => <span>{row.when}</span>
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status,
      priority: 2,
      cell: (row) => <Badge variant={sessionStatusTone(row.statusRaw)}>{row.status}</Badge>
    },
    {
      id: "recording",
      header: "Recording",
      sortable: false,
      priority: 3,
      cell: (row) => <span className="text-sm text-ink-muted">{row.recording}</span>
    }
  ];
}

export function useParticipantSessionsCollection(
  rows: ParticipantSessionRow[]
): UseCollectionResult<ParticipantSessionRow> {
  const filters = useMemo(() => buildParticipantSessionsFilterDefs(rows), [rows]);
  const columns = useMemo(() => buildParticipantSessionsCollectionColumns(), []);

  return useCollection<ParticipantSessionRow>({
    mode: "client",
    rows,
    columns,
    filters,
    pageSize: PARTICIPANT_SESSIONS_PAGE_SIZE,
    urlSync: false,
    initialState: { sort: PARTICIPANT_SESSIONS_DEFAULT_SORT },
    getRowKey: (row) => row.id
  });
}